import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  COUNTRIES,
  countryLabel,
  findCountryByIso,
  flagEmoji,
  flagUrl,
  searchCountries,
} from "../../constants/countries";
import styles from "./CountryCodeSelect.module.scss";

/** Flag image, or the flag emoji when the image cannot load (offline, blocked CDN). */
export const CountryFlag = ({ iso, className = "", lazy = false }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [iso]);

  if (failed) {
    return (
      <span className={`${styles["flag-emoji"]} ${className}`} aria-hidden="true">
        {flagEmoji(iso)}
      </span>
    );
  }
  return (
    <img
      src={flagUrl(iso)}
      alt=""
      aria-hidden="true"
      loading={lazy ? "lazy" : undefined}
      className={`${styles["flag"]} ${className}`}
      onError={() => setFailed(true)}
    />
  );
};

const MENU_WIDTH = 280;
const MENU_MAX_HEIGHT = 320;
const MENU_MIN_HEIGHT = 160;
const GAP = 4;
const EDGE = 8;

/**
 * Country dial-code picker: flag + dial code button, and a menu listing every country (flag,
 * dial code, name) with a search box. Typing a country name (UI language or English), an alias
 * ("UAE") or a dial code ("964") filters the list. Arrow keys move, Enter picks, Escape closes.
 *
 * The menu renders in a portal with fixed positioning, so dialogs and containers with
 * `overflow: hidden` never clip it.
 *
 * Props:
 *  - value:     selected country ISO code, e.g. "iq"
 *  - onChange:  (country: { iso, code, name }) => void
 *  - disabled:  optional
 *  - className: optional class for the trigger box (e.g. a page's `country-selector`); without
 *               it the picker draws its own bordered box
 *  - style:     optional inline style for the trigger box
 *  - id:        optional id of the trigger button
 *
 * The same component exists in the doctor, lab, imaging, nurse and physiotherapy apps
 * (src/components/country-code-select). Keep them in step.
 */
const CountryCodeSelect = ({
  value,
  onChange,
  disabled = false,
  className = "",
  style,
  id,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const selected = findCountryByIso(value);
  const listId = useId();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [position, setPosition] = useState(null);

  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  // Scroll the active option into view only after keyboard moves / opening, not mouse hover.
  const scrollToActiveRef = useRef(false);

  // `language` re-creates the label function when the UI language changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const label = useCallback((country) => countryLabel(i18n, country), [i18n, language]);

  // A–Z by the name shown in the current language; built only while the menu is open.
  const sortedCountries = useMemo(
    () =>
      isOpen
        ? [...COUNTRIES].sort((a, b) => label(a).localeCompare(label(b), language))
        : [],
    [isOpen, label, language],
  );
  const results = useMemo(
    () => searchCountries(sortedCountries, query, label),
    [sortedCountries, query, label],
  );

  const updatePosition = useCallback(() => {
    const anchor = rootRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const rtl = window.getComputedStyle(anchor).direction === "rtl";
    const width = Math.min(MENU_WIDTH, window.innerWidth - EDGE * 2);
    const left = Math.max(
      EDGE,
      Math.min(rtl ? rect.right - width : rect.left, window.innerWidth - width - EDGE),
    );
    const spaceBelow = window.innerHeight - rect.bottom - GAP - EDGE;
    const spaceAbove = rect.top - GAP - EDGE;
    const openAbove = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
    const maxHeight = Math.max(
      MENU_MIN_HEIGHT,
      Math.min(MENU_MAX_HEIGHT, openAbove ? spaceAbove : spaceBelow),
    );
    setPosition({
      left,
      width,
      maxHeight,
      rtl,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + GAP }
        : { top: rect.bottom + GAP }),
    });
  }, []);

  const close = useCallback((focusTrigger) => {
    setIsOpen(false);
    setQuery("");
    setPosition(null);
    if (focusTrigger) triggerRef.current?.focus();
  }, []);

  const toggle = () => {
    if (disabled) return;
    if (isOpen) {
      close(false);
      return;
    }
    setQuery("");
    setIsOpen(true);
  };

  // Close when the picker becomes disabled (e.g. while a form is submitting).
  useEffect(() => {
    if (disabled && isOpen) close(false);
  }, [disabled, isOpen, close]);

  // Position the menu before paint and follow the trigger on scroll / resize.
  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    updatePosition();
    const onScroll = (event) => {
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      updatePosition();
    };
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [isOpen, updatePosition]);

  // On open: focus the search box and start on the selected country.
  useEffect(() => {
    if (!isOpen || !position) return;
    searchRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, Boolean(position)]);

  // Highlight the selected country on open, the first match while searching.
  useEffect(() => {
    if (!isOpen) return;
    const selectedIndex = query
      ? 0
      : results.findIndex((country) => country.iso === selected.iso);
    scrollToActiveRef.current = true;
    setActiveIndex(Math.max(0, selectedIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, query]);

  useEffect(() => {
    if (!isOpen || !scrollToActiveRef.current) return;
    const list = listRef.current;
    const item = list?.children[activeIndex];
    // The list mounts one render after opening (once the menu is positioned).
    if (!list || !item) return;
    scrollToActiveRef.current = false;
    if (item.offsetTop < list.scrollTop) {
      list.scrollTop = item.offsetTop;
    } else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
  }, [isOpen, activeIndex, position]);

  // Close on a click / touch outside the trigger and the menu.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      close(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [isOpen, close]);

  const select = (country) => {
    if (country.iso !== selected.iso) onChange?.(country);
    close(true);
  };

  const moveActive = (step) => {
    if (results.length === 0) return;
    scrollToActiveRef.current = true;
    setActiveIndex((index) => Math.min(results.length - 1, Math.max(0, index + step)));
  };

  const handleMenuKeyDown = (event) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        moveActive(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        moveActive(-1);
        break;
      case "PageDown":
        event.preventDefault();
        moveActive(8);
        break;
      case "PageUp":
        event.preventDefault();
        moveActive(-8);
        break;
      case "Enter":
        // Never submit the surrounding form from the search box.
        event.preventDefault();
        if (results[activeIndex]) select(results[activeIndex]);
        break;
      case "Escape":
        // Close only the menu, not a dialog listening for Escape on the document.
        event.preventDefault();
        event.stopPropagation();
        event.nativeEvent.stopPropagation();
        close(true);
        break;
      case "Tab":
        event.preventDefault();
        close(true);
        break;
      default:
        break;
    }
  };

  const optionId = (country) => `${listId}-${country.iso}`;
  const activeCountry = results[activeIndex];
  const searchPlaceholder = t("countries.searchPlaceholder", {
    defaultValue: "Search country or code",
  });

  const menu =
    isOpen && position
      ? createPortal(
          <div
            ref={menuRef}
            className={styles["menu"]}
            dir={position.rtl ? "rtl" : "ltr"}
            style={{
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
              top: position.top,
              bottom: position.bottom,
            }}
            onKeyDown={handleMenuKeyDown}
          >
            <div className={styles["search"]}>
              <Search size={16} className={styles["search-icon"]} aria-hidden="true" />
              <input
                ref={searchRef}
                type="text"
                className={styles["search-input"]}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                role="combobox"
                aria-expanded="true"
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={activeCountry ? optionId(activeCountry) : undefined}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            {results.length > 0 ? (
              <ul ref={listRef} id={listId} role="listbox" className={styles["list"]}>
                {results.map((country, index) => {
                  const isSelected = country.iso === selected.iso;
                  return (
                    <li
                      key={country.iso}
                      id={optionId(country)}
                      role="option"
                      aria-selected={isSelected}
                      className={[
                        styles["option"],
                        isSelected ? styles["option-selected"] : "",
                        index === activeIndex ? styles["option-active"] : "",
                      ].join(" ")}
                      // Keep focus in the search box while clicking an option.
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => select(country)}
                    >
                      <CountryFlag iso={country.iso} lazy />
                      <span className={styles["option-code"]} dir="ltr">
                        {country.code}
                      </span>
                      <span className={styles["option-name"]}>{label(country)}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className={styles["empty"]} role="status">
                {t("countries.noResults", { defaultValue: "No countries found" })}
              </p>
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={className || styles["country-select"]}
      style={style}
      data-country-dropdown
    >
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={className ? styles["trigger-bare"] : styles["trigger"]}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !isOpen && !disabled) {
            event.preventDefault();
            toggle();
          }
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t("countries.countryCodeAria", {
          country: label(selected),
          code: selected.code,
          defaultValue: "Country code: {{country}} {{code}}",
        })}
      >
        <CountryFlag iso={selected.iso} />
        <span className={styles["code"]} dir="ltr">
          {selected.code}
        </span>
        <ChevronDown
          size={14}
          className={`${styles["arrow"]} ${isOpen ? styles["arrow-open"] : ""}`}
          aria-hidden="true"
        />
      </button>
      {menu}
    </div>
  );
};

export default CountryCodeSelect;
